import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI, gameAPI, userAPI } from "../services/api";
import TermsModal from "../components/TermsModal";
import { motion, AnimatePresence } from 'framer-motion';

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [hotelType, setHotelType] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [step, setStep] = useState(1); // 1 for account details, 2 for hotel details
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [username, setUserName] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false); // State for the Terms & Conditions modal
  const [showGuestWarning, setShowGuestWarning] = useState(false); // State for the Guest Mode warning modal

  const hotelTypes = [
    {
      id: "budget",
      name: "Budget Hotel",
      description: "Affordable accommodations with basic amenities",
    },
    {
      id: "boutique",
      name: "Boutique Hotel",
      description: "Stylish, intimate hotels with unique character",
    },
    {
      id: "luxury",
      name: "Luxury Resort",
      description: "High-end accommodations with premium amenities",
    },
    {
      id: "business",
      name: "Business Hotel",
      description: "Practical amenities for business travelers",
    },
  ];

  const handleNextStep = () => {
    // Reset any previous errors
    setError("");

    // Validate first step
    if (!email) {
      setError("Email is required");
      return;
    }

    if (!username) {
      setError("Username is required");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setStep(2);
  };

  const handleRegister = async () => {
    // Reset any previous errors
    setError("");

    // Validate second step
    if (!hotelName) {
      setError("Hotel name is required");
      return;
    }

    if (!hotelType) {
      setError("Please select a hotel type");
      return;
    }

    if (!agreeTerms) {
      setError("You must agree to the terms and conditions");
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Register user with backend using the API service
      // This will automatically store the token and user data in localStorage within the API service
      const registerResponse = await authAPI.register(
        email,
        username,
        password,
        hotelName,
        hotelType
      );

      // Step 2: Create initial game state
      const initialGameState = createInitialGameState(hotelName, hotelType);

      // Step 3: Save the initial game state to the server
      try {
        // No need to update hotel data again, as it's already handled in the registration process
        const timeStampVal = new Date().getTime();

        // Then, save the initial game state to the server
        await gameAPI.saveGame(
          `${hotelName} - ${timeStampVal}`,
          initialGameState
        );
      } catch (saveError) {
        console.error("Error saving initial game state:", saveError);
        // Continue with local storage even if server save fails
      }

      // Save to localStorage as fallback
      localStorage.setItem("gameState", JSON.stringify(initialGameState));

      // Redirect to game
      navigate("/game");
    } catch (error) {
      console.error("Registration error:", error);
      setError(
        error.response?.data?.message ||
          "Registration failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handleGuestMode = () => {
    // Create a guest user token or use a predefined guest token
    const guestToken = 'guest_' + Math.random().toString(36).substr(2, 9);
    
    // Store guest token
    localStorage.setItem('token', guestToken);
    
    // Store guest user information
    localStorage.setItem('user', JSON.stringify({
      id: 'guest_' + Math.random().toString(36).substr(2, 9),
      username: 'Guest Player',
      isGuest: true
    }));
    
    // Create and store initial game state with a default hotel
    const guestHotelName = "Guest Hotel";
    const guestHotelType = "budget";
    const initialGameState = createInitialGameState(guestHotelName, guestHotelType);
    localStorage.setItem("gameState", JSON.stringify(initialGameState));
    
    // Navigate to game
    navigate('/game');
  };

  const createInitialGameState = (hotelName, hotelType) => {
    // Same starting values for all hotel types
    const initialMoney = 10000;
    const initialReputation = 3.0;

    // Starting rooms (same for all types)
    const initialRooms = [
      {
        id: 1,
        type: "Standard",
        price: 80,
        occupied: false,
        quality: 1,
        guest: null,
      },
      {
        id: 2,
        type: "Standard",
        price: 80,
        occupied: false,
        quality: 1,
        guest: null,
      },
    ];

    // Starting staff (same for all types)
    const initialStaff = [
      {
        id: 1,
        name: "Jane",
        role: "Receptionist",
        salary: 100,
        skill: 2,
        happiness: 4,
      },
    ];

    // Starting facilities (same for all types)
    const initialFacilities = [
      {
        id: 1,
        name: "Lobby",
        level: 1,
        upkeep: 50,
      },
    ];

    return {
      name: hotelName,
      type: hotelType,
      day: 1,
      money: initialMoney,
      reputation: initialReputation,
      rooms: initialRooms,
      staff: initialStaff,
      facilities: initialFacilities,
      guests: [
        {
          id: "placeholder",
          name: "Placeholder Guest",
          roomId: -1,
          checkoutDay: -1,
          happiness: 3,
          type: "placeholder",
          preferences: {
            roomQuality: 3,
            foodQuality: 3,
            serviceQuality: 3,
          },
        },
      ],
      policies: {
        petFriendly: false,
        earlyCheckin: false,
        lateCheckout: false,
        freeCancellation: false,
        breakfastIncluded: false,
      },
      competitors: [
        {
          id: 0,
          name: "Placeholder Hotel",
          reputation: 3.0,
          rooms: 5,
          occupancyRate: 0.5,
          avgRoomPrice: 100,
          facilities: ["Restaurant"],
          marketingLevel: 1,
          marketingActive: false,
          marketingEffort: 0,
        },
      ],
      marketShare: 0,
      achievements: [
        {
          id: "placeholder_achievement",
          title: "Placeholder Achievement",
          description: "Placeholder for Firebase storage",
          unlocked: false,
        },
      ],
      marketData: [{ day: 1, share: 0, reputation: initialReputation }],
    };
  };

  const buildingBlockVariants = {
    initial: { opacity: 0, y: -20 },
    animate: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.03,
      },
    }),
  };

  return (
    <motion.div 
      className="relative w-full min-h-screen flex justify-center items-center bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden px-4 py-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Skyline background */}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-2/5 bg-gradient-to-b from-blue-600/10 to-blue-600/30"
        style={{
          clipPath:
            "polygon(0% 60%, 5% 50%, 10% 55%, 15% 40%, 20% 45%, 25% 30%, 30% 40%, 35% 25%, 40% 40%, 45% 20%, 50% 25%, 55% 15%, 60% 35%, 65% 25%, 70% 40%, 75% 20%, 80% 35%, 85% 30%, 90% 45%, 95% 35%, 100% 50%, 100% 100%, 0% 100%)",
        }}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
      >
        {/* Animated stars/lights in the skyline */}
        {[...Array(15)].map((_, i) => (
          <motion.div 
            key={i}
            className="absolute w-1 h-1 bg-yellow-400 rounded-full"
            style={{
              left: `${5 + (i * 6)}%`,
              bottom: `${20 + Math.random() * 60}%`,
            }}
            animate={{
              opacity: [0.2, 1, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 1.5 + Math.random() * 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </motion.div>

      {/* Guest Mode Warning Modal */}
      {showGuestWarning && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="bg-white rounded-2xl p-4 md:p-8 max-w-md w-full shadow-2xl mx-4"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="text-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 text-yellow-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800">Guest Mode Limitations</h2>
              <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                Guest mode allows you to play the game, but:
                <ul className="list-disc list-inside text-left mt-2 space-y-1 md:space-y-2">
                  <li>Progress won't be saved permanently</li>
                  <li>Some features may be restricted</li>
                  <li>You won't have a persistent account</li>
                </ul>
              </p>
              <div className="flex justify-center space-x-3 md:space-x-4">
                <button
                  onClick={() => setShowGuestWarning(false)}
                  className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowGuestWarning(false);
                    handleGuestMode();
                  }}
                  className="px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm md:text-base"
                >
                  Continue as Guest
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Main content */}
      <div className="flex justify-center items-center w-full z-10 p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Brand container */}
          <div className="flex flex-col items-center mb-6">
            {/* Building icon */}
            <a href="/"><img alt="Hotel Hero Logo" class="w-16 h-16 object-contain mr-3" src="/logo.png"/></a>

            {/* Title */}
            <motion.h1 
              className="font-sans text-3xl md:text-4xl font-extrabold text-white mb-1 drop-shadow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Hotel
              <motion.span 
                className="text-orange-500 relative inline-block pb-1 border-b-2 border-orange-500"
                initial={{ width: 0 }}
                animate={{ width: "auto" }}
                transition={{ delay: 0.9, duration: 0.4 }}
              >
                Hero
              </motion.span>
            </motion.h1>

            {/* Tagline */}
            <motion.p 
              className="text-white/80 text-sm md:text-base mb-3 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              Build Your Empire, Crush Your Rivals
            </motion.p>
          </div>

          {/* Register form */}
          <motion.div 
            className="bg-white rounded-2xl p-6 md:p-8 w-full shadow-md"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <motion.h2 
              className="text-xl md:text-2xl font-semibold mb-4 md:mb-5 text-slate-800 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {step === 1 ? "Create Your Account" : "Setup Your Hotel"}
            </motion.h2>

            {/* Step indicator */}
            <div className="flex items-center justify-center mb-5 md:mb-6">
              <div
                className={`h-2 w-14 md:w-16 rounded-full ${
                  step === 1 ? "bg-blue-600" : "bg-blue-200"
                }`}
              ></div>
              <div className="mx-2 text-xs text-slate-400">Step {step}/2</div>
              <div
                className={`h-2 w-14 md:w-16 rounded-full ${
                  step === 2 ? "bg-blue-600" : "bg-blue-200"
                }`}
              ></div>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div 
                  className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-xs md:text-sm"
                  initial={{ opacity: 0, height: 0, margin: 0, padding: 0 }}
                  animate={{ opacity: 1, height: "auto", margin: "0 0 1rem", padding: "0.75rem" }}
                  exit={{ opacity: 0, height: 0, margin: 0, padding: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {step === 1 ? (
              /* Step 1: Account Details */
              <>
                <div className="space-y-4">
                  <input
                    className="w-full py-2.5 md:py-3 px-4 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50"
                    placeholder="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="off"
                    required
                  />

                  <input
                    className="w-full py-2.5 md:py-3 px-4 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50"
                    placeholder="Username"
                    type="text"
                    value={username}
                    autoComplete="off"
                    onChange={(e) => setUserName(e.target.value)}
                    required
                  />

                  <input
                    className="w-full py-2.5 md:py-3 px-4 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50"
                    placeholder="Password"
                    type="password"
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />

                  <input
                    className="w-full py-2.5 md:py-3 px-4 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50"
                    placeholder="Confirm Password"
                    type="password"
                    autoComplete="off"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <motion.button 
                  className="w-full py-2.5 md:py-3 px-5 bg-blue-600 text-white border-none rounded-lg text-sm md:text-base font-medium cursor-pointer text-center hover:bg-blue-700 transition-colors mt-6"
                  onClick={handleNextStep}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Continue
                </motion.button>

                <motion.div 
                  className="mt-5 text-center text-xs md:text-sm text-slate-500 flex flex-col space-y-2 md:space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <div>
                    Already have an account?
                    <motion.span 
                      className="text-blue-600 ml-1 hover:underline cursor-pointer relative inline-block group"
                      onClick={() => navigate('/login')}
                      whileHover="hover"
                    >
                      Log In
                      <motion.span
                        className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500"
                        initial={{ scaleX: 0 }}
                        variants={{
                          hover: {
                            scaleX: 1,
                            transition: { duration: 0.3 }
                          }
                        }}
                        style={{ originX: 0 }}
                      />
                    </motion.span>
                  </div>
                  
                  <motion.div 
                    className="text-green-600 hover:underline cursor-pointer relative inline-block group"
                    onClick={() => setShowGuestWarning(true)}
                    whileHover="hover"
                  >
                    Play as Guest
                    <motion.span
                      className="absolute bottom-0 left-0 w-full h-0.5 bg-green-500"
                      initial={{ scaleX: 0 }}
                      variants={{
                        hover: {
                          scaleX: 1,
                          transition: { duration: 0.3 }
                        }
                      }}
                      style={{ originX: 0 }}
                    />
                  </motion.div>
                </motion.div>
              </>
            ) : (
              /* Step 2: Hotel Details */
              <>
                <input
                  className="w-full py-2.5 md:py-3 px-4 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50 mb-5"
                  placeholder="Hotel Name"
                  value={hotelName}
                  onChange={(e) => setHotelName(e.target.value)}
                  required
                />

                <div className="mb-5">
                  <label className="block text-xs md:text-sm font-medium text-slate-700 mb-2">
                    Hotel Type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                    {hotelTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`cursor-pointer border rounded-lg p-2 md:p-3 transition-all hover:border-blue-400 ${
                          hotelType === type.id
                            ? "border-blue-600 bg-blue-50"
                            : "border-slate-200"
                        }`}
                        onClick={() => setHotelType(type.id)}
                      >
                        <div className="font-medium text-sm md:text-base text-slate-800">
                          {type.name}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {type.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center mb-5 md:mb-6">
                  <div
                    className={`w-4 md:w-5 h-4 md:h-5 border border-slate-300 rounded mr-2 md:mr-3 flex items-center justify-center ${
                      agreeTerms ? "bg-blue-600 border-blue-600" : ""
                    }`}
                    onClick={() => setAgreeTerms(!agreeTerms)}
                  >
                    {agreeTerms && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-2.5 md:h-3 w-2.5 md:w-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <label className="text-slate-600 text-xs md:text-sm cursor-pointer">
                    I agree to the{" "}
                    <span
                      className="text-blue-600 hover:underline cursor-pointer"
                      onClick={handleTermsClick}
                    >
                      Terms & Conditions
                    </span>
                  </label>
                </div>

                <div className="flex gap-2 md:gap-3">
                  <motion.button
                    className="py-2.5 md:py-3 px-4 md:px-5 bg-slate-200 text-slate-700 border-none rounded-lg text-sm md:text-base font-medium cursor-pointer text-center hover:bg-slate-300 transition-colors flex-1"
                    onClick={() => setStep(1)}
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back
                  </motion.button>

                  <motion.button
                    className={`py-2.5 md:py-3 px-4 md:px-5 bg-blue-600 text-white border-none rounded-lg text-sm md:text-base font-medium cursor-pointer text-center hover:bg-blue-700 transition-colors flex-1 ${
                      isLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                    onClick={handleRegister}
                    disabled={isLoading}
                    whileHover={!isLoading ? { scale: 1.02 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </div>
                    ) : (
                      "Create Hotel"
                    )}
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>

      <motion.div 
        className="absolute bottom-2 md:bottom-5 w-full text-center text-white/60 text-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
      >
        &copy; 2025 HotelHero. All rights reserved.
      </motion.div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </motion.div>
  );
};

export default Register;